import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  BaseExceptions,
  getMessageFromErrorResponse,
  isBaseError,
} from '@/api/v4/base-fetch';
import type { ShareOrganizationBody } from '@/api/v4/organization.api';
import { shareOrganization } from '@/api/v4/organization.api';
import { QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';

interface Payload extends ShareOrganizationBody {
  organizationId: string;
}

export const useShareCompany = () => {
  const { t } = useTranslation('default');
  const queryClient = useQueryClient();

  const shareCompanyMutation = useMutation({
    mutationFn: async (payload: Payload) => {
      const { organizationId, ...data } = payload;

      return shareOrganization(organizationId, data);
    },
    onSuccess: async (_, variables) => {
      const { organizationId } = variables;
      notify({ message: t`share.shareOrganizationSuccess`, withIcon: true });

      await queryClient.invalidateQueries({ queryKey: [QueryKey.TeamOptions] });
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.Users, organizationId],
      });
      queryClient.removeQueries({ queryKey: [QueryKey.TeamOptions] });
      queryClient.removeQueries({ queryKey: [QueryKey.Users, organizationId] });
    },
    onError: error => {
      if (isBaseError(error)) {
        if (error.response.status === BaseExceptions.BAD_REQUEST) {
          const message = getMessageFromErrorResponse(error);
          notify({ message: message ?? t`unknownError` });
          return;
        }
      }
      notify({ message: t`unknownError` });
    },
  });

  const shareCompany = (payload: Payload) =>
    shareCompanyMutation.mutateAsync(payload);

  return {
    shareCompany,
    isError: shareCompanyMutation.isError,
  };
};
